import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Copy, Button } from '@honda-canada/design-system-react';
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import { useTranslation } from 'react-i18next';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import themeStyles from './styles/FinancePaymentDetails.styles';
import { useFinance } from '../SelectProductsContext/SelectProductsProvider';
import InfoModal from '../../utils/components/InfoModal';
import useDropdownOptions from '../../hooks/useDropdownOptions';
import getFinancingDisclaimerParams from '../../utils/financingDisclaimerParams';

const PaymentWrapper = themeStyles.apply(Box, 'PaymentWrapper');
const FrequencyWrapper = themeStyles.apply(Box, 'FrequencyWrapper');
const TermWrapper = themeStyles.apply(Box, 'TermWrapper');
const TitleCopy = themeStyles.apply(Copy, 'TitleCopy');
const PriceCopy = themeStyles.apply(Copy, 'PriceCopy');
const FinanceAndCTAContainer = themeStyles.apply(Box, 'FinanceAndCTAContainer');
const FinanceDetailsContainer = themeStyles.apply(Box, 'FinanceDetailsContainer');
const ButtonContainer = themeStyles.apply(Box, 'ButtonContainer');

const FinancePaymentDetails = ({ pricing, finance, isLoading, showSellingPrice }) => {
  const [showModal, setShowModal] = useState(false);
  const [financeContext] = useFinance();
  const { t } = useTranslation();
  const { sitecoreContext } = useSitecoreContext() || {};
  const language = sitecoreContext?.language || 'en';
  const { dropdownOptions } = useDropdownOptions();

  if (!pricing || !finance) return null;
  const selectedOption = financeContext.options.toLowerCase();
  const selectedOptionValue = showSellingPrice ? finance[selectedOption].total : finance[selectedOption].value;

  const disclaimerParams = getFinancingDisclaimerParams(finance, pricing, financeContext, language, dropdownOptions);
  const disclaimerTooltip = t('Shared.Common.paymentDisclaimerText', disclaimerParams);

  let financeLabel;

  switch (selectedOption) {
    case 'weekly':
      financeLabel = 'Sub-apps.PriceAndFinance.weeklyFinanceLabel';
      break;
    case 'monthly':
      financeLabel = 'Sub-apps.PriceAndFinance.monthlyFinanceLabel';
      break;
    case 'biweekly':
      financeLabel = 'Sub-apps.PriceAndFinance.biWeeklyFinanceLabel';
      break;
    default:
      financeLabel = null;
  }

  return (
    <FinanceAndCTAContainer data-testid="cy-payment-options">
      {pricing?.value && selectedOptionValue && !isLoading && (
        <FinanceDetailsContainer>
          <PaymentWrapper data-testid="cy-payment-wrapper">
            <FrequencyWrapper data-testid="cy-payment-frequency-wrapper">
              <TitleCopy size="small" data-testid="cy-payment-frequency-label">
                {t(financeLabel)}
              </TitleCopy>
              <PriceCopy size="small" mr={['zero', 'zero', 'xxs']} data-testid="cy-payment-frequency-value">
                {`${formatPrice(selectedOptionValue, language, 2)}`}
                <InfoModal showModal={showModal} setShowModal={setShowModal} content={disclaimerTooltip} />
              </PriceCopy>
            </FrequencyWrapper>
            <TermWrapper>
              <TitleCopy size="small" data-testid="cy-payment-term-label">
                {t('Sub-apps.PriceAndFinance.termByMonthLabel')}
              </TitleCopy>
              <PriceCopy size="small" data-testid="cy-payment-term-value">
                {`${finance[selectedOption].termOptionsDefault} @ ${finance[selectedOption].apr}%`}
              </PriceCopy>
            </TermWrapper>
          </PaymentWrapper>
        </FinanceDetailsContainer>
      )}
      <ButtonContainer>
        <Button aling="center" styling="primary" as="a" href={t('Shared.Common.dealersPageUrl')}>
          {t('Sub-apps.PriceAndFinance.findLocalDealerButton')}
        </Button>
      </ButtonContainer>
    </FinanceAndCTAContainer>
  );
};

FinancePaymentDetails.propTypes = {
  pricing: PropTypes.shape({
    value: PropTypes.number,
  }),
  finance: PropTypes.shape({
    monthly: PropTypes.shape({
      value: PropTypes.number,
      informationalApr: PropTypes.number,
      freightPdi: PropTypes.number,
      fees: PropTypes.number,
    }),
    biweekly: PropTypes.shape({
      value: PropTypes.number,
      informationalApr: PropTypes.number,
      freightPdi: PropTypes.number,
      fees: PropTypes.number,
    }),
    weekly: PropTypes.shape({
      value: PropTypes.number,
      informationalApr: PropTypes.number,
      freightPdi: PropTypes.number,
      fees: PropTypes.number,
    }),
  }),
  isLoading: PropTypes.bool,
  showSellingPrice: PropTypes.bool,
};
export default FinancePaymentDetails;
